// @flow

import React, { type Node } from 'react';
import bemify, { toModifiers } from 'bemify-js';

import Button from 'app/common/components/Button';
import ButtonGroup from 'app/common/components/ButtonGroup';
import ConfettiStateful from 'app/common/components/ConfettiStateful';
import Modal from 'app/common/components/Modal';
import InfoBlock, {
  InfoBlockDescription,
  InfoBlockStaticImage,
  InfoBlockTitle,
} from 'app/common/components/InfoBlock';
import 'app/billing/components/ChangePlanCompleteModal.scss';

import type { KindType } from 'app/common/types';

const bem = bemify('change-plan-complete-modal');

export type Props = {
  className?: string,
  content: Node,
  heading: string,
  imgAlt: string,
  imgSrc: string,
  isPartying: boolean,
  kind?: KindType,
  onClose: () => void,
  onLeave: () => void,
  onPrimaryButtonClick?: () => void,
  primaryButtonHref?: string,
  primaryButtonText?: string,
  secondaryButtonHref?: string,
  secondaryButtonText?: string,
  subHeading?: string,
};

const ChangePlanCompleteModal = (props: Props) => {
  const img = props.imgSrc && props.imgAlt && (
    <InfoBlockStaticImage alt={props.imgAlt} src={props.imgSrc} />
  );

  const secondaryButton = props.secondaryButtonText &&
    props.secondaryButtonHref && (
      <Button
        href={props.secondaryButtonHref}
        kind="secondary large min-width"
        onClick={props.onLeave}
        tag="Link"
      >
        {props.secondaryButtonText}
      </Button>
    );

  const primaryButton = (
    <Button
      href={props.onPrimaryButtonClick ? undefined : props.primaryButtonHref}
      kind="primary large min-width"
      onClick={props.onPrimaryButtonClick || props.onLeave}
      tag={props.onPrimaryButtonClick ? 'button' : 'Link'}
    >
      {props.primaryButtonText}
    </Button>
  );

  const content = (
    <InfoBlock kind="bare centered">
      {props.isPartying && <ConfettiStateful />}
      {img}
      <InfoBlockTitle>{props.heading}</InfoBlockTitle>
      <InfoBlockDescription>{props.content}</InfoBlockDescription>
      <ButtonGroup
        kindQueries={{
          '(max-width: 500px)': 'reversed',
          '(min-width: 500px)': 'horizontal justify-center',
        }}
      >
        {secondaryButton}
        {primaryButton}
      </ButtonGroup>
    </InfoBlock>
  );

  return (
    <Modal
      className={bem(toModifiers(props.kind))}
      closeOnEsc={false}
      closeOnOutsideClick={false}
      contentClassName=""
      hideCloseButton={true}
      isOpened={true}
      onClose={props.onClose}
    >
      {content}
    </Modal>
  );
};

ChangePlanCompleteModal.defaultProps = {
  imgAlt: 'Plan changed',
  imgSrc: 'images/applings/zapier-celebrate.svg',
  onClose: () => {},
  onLeave: () => {},
};

export default ChangePlanCompleteModal;
